import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { computeAps, percentageToLevel } from "@/lib/aps";
import { Plus, Trash2, Info, ArrowRight, Loader2, Calculator } from "lucide-react";

function levelColor(level) {
  if (level >= 6) return "bg-emerald-100 text-emerald-700";
  if (level >= 4) return "bg-[#F5C32C]/20 text-[#8a6d0b]";
  if (level >= 1) return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-400";
}

export default function Marks() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [rows, setRows] = useState([{ subject: "", percentage: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/subjects").then(({ data }) => setSubjects(data.subjects));
  }, []);

  useEffect(() => {
    if (user && user.marks && user.marks.length) {
      setRows(user.marks.map((m) => ({ subject: m.subject, percentage: String(m.percentage) })));
    }
  }, [user]);

  const { aps } = computeAps(rows);
  const usedSubjects = rows.map((r) => r.subject).filter(Boolean);

  const addRow = () => setRows([...rows, { subject: "", percentage: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const update = (i, key, val) => {
    const next = [...rows];
    next[i][key] = val;
    setRows(next);
  };

  const validRows = rows.filter(
    (r) => r.subject && r.percentage !== "" && Number(r.percentage) >= 0 && Number(r.percentage) <= 100
  );

  const save = async () => {
    if (validRows.length < 6) {
      toast.error("Please enter at least 6 subjects with valid percentages (0–100).");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/marks", {
        marks: validRows.map((r) => ({ subject: r.subject, percentage: Number(r.percentage) })),
      });
      await refresh();
      toast.success(`APS calculated: ${data.aps}. You qualify for ${data.qualifying_count} course(s)!`);
      navigate("/dashboard/courses");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-8">
        {/* Marks entry */}
        <div className="lg:col-span-2 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Step 1</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1">Enter your marks</h1>
          <p className="text-slate-600 mt-2 max-w-lg">
            Select each subject and enter the percentage you achieved. Your NSC level is calculated automatically.
          </p>

          <div className="mt-8 space-y-3" data-testid="marks-rows">
            <AnimatePresence>
              {rows.map((row, i) => {
                const level = row.percentage !== "" ? percentageToLevel(row.percentage) : 0;
                const available = subjects.filter(
                  (s) => !usedSubjects.includes(s) || s === row.subject
                );
                return (
                  <motion.div
                    key={i}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-2 sm:gap-3 shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <Select value={row.subject} onValueChange={(v) => update(i, "subject", v)}>
                        <SelectTrigger data-testid={`subject-select-${i}`} className="border-slate-300">
                          <SelectValue placeholder="Choose a subject" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {available.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-16 sm:w-24 shrink-0">
                      <Input
                        type="number" min="0" max="100" placeholder="%"
                        value={row.percentage}
                        onChange={(e) => update(i, "percentage", e.target.value)}
                        data-testid={`percentage-input-${i}`}
                        className="text-center"
                      />
                    </div>
                    <div className={`w-10 sm:w-16 shrink-0 text-center py-2 rounded-md text-sm font-bold ${levelColor(level)}`}
                      data-testid={`level-badge-${i}`}>
                      {level ? `L${level}` : "—"}
                    </div>
                    <button
                      onClick={() => removeRow(i)}
                      data-testid={`remove-row-${i}`}
                      className="text-slate-400 hover:text-rose-500 transition-colors p-1 sm:p-2 shrink-0"
                      aria-label="Remove subject"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          <Button variant="outline" onClick={addRow} data-testid="add-subject-btn"
            className="mt-4 border-dashed border-[#0B2447]/30 text-[#0B2447]">
            <Plus className="w-4 h-4 mr-2" /> Add another subject
          </Button>
        </div>

        {/* Sticky APS card */}
        <div>
          <div className="sticky top-24">
            <motion.div
              layout
              className="bg-[#0B2447] text-white rounded-xl p-6 shadow-xl"
              data-testid="aps-card"
            >
              <div className="flex items-center gap-2 text-[#F5C32C]">
                <Calculator className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">Your APS Score</span>
              </div>
              <motion.div
                key={aps}
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                className="font-display text-6xl font-bold mt-3"
                data-testid="aps-total-display"
              >
                {aps}
              </motion.div>
              <div className="flex items-center gap-1.5 mt-2 text-white/60 text-xs">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" />
                      Best 6 subjects · Life Orientation excluded
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Life Orientation is excluded from the total APS as per university standards.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="mt-5 pt-5 border-t border-white/10 text-sm text-white/70">
                Subjects entered: <span className="text-white font-semibold">{validRows.length}</span>
              </div>
              <Button
                onClick={save}
                disabled={saving || validRows.length < 6}
                data-testid="calculate-save-btn"
                className="mt-5 w-full bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>See Qualifying Courses <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
              {validRows.length < 6 && (
                <p className="mt-3 text-xs text-white/50 text-center">Enter at least 6 subjects to continue.</p>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
